import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  fetchSideQuests,
  createSideQuest,
  updateSideQuest,
  deleteSideQuest,
} from '../../actions/planningAction';
import './PlanningSideQuests.css';

const TITLE_LIMIT = 200;
const NOTE_LIMIT = 2000;

const PROMPTS = [
  'See the northern lights in person',
  'Learn to play the piano',
  'Live in another country for a year',
  'Take my parents on a trip they will never forget',
  'Get my pilot license',
];

export default function PlanningSideQuests() {
  const dispatch = useDispatch();
  const [quests, setQuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editNote, setEditNote] = useState('');
  const [confirmDel, setConfirmDel] = useState(null);
  const composerRef = useRef(null);
  const promptIdx = useRef(Math.floor(Math.random() * PROMPTS.length));

  const load = useCallback(() => {
    setLoading(true);
    dispatch(fetchSideQuests())
      .then(d => setQuests(d.sideQuests || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dispatch]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (composerOpen && composerRef.current) composerRef.current.focus();
  }, [composerOpen]);

  const addQuest = async () => {
    const t = newTitle.trim();
    if (!t || saving) return;
    setSaving(true);
    try {
      const d = await dispatch(createSideQuest({ title: t, note: newNote.trim() }));
      setQuests(p => [d.sideQuest, ...p]);
      setNewTitle('');
      setNewNote('');
      setComposerOpen(false);
    } catch (_) {}
    setSaving(false);
  };

  const openEdit = (q) => {
    setEditing(q);
    setEditTitle(q.title);
    setEditNote(q.note || '');
  };

  const saveEdit = async () => {
    if (!editing) return;
    const t = editTitle.trim();
    if (!t) return;
    const id = editing._id;
    try {
      const d = await dispatch(updateSideQuest(id, { title: t, note: editNote.trim() }));
      setQuests(p => p.map(q => q._id === id ? d.sideQuest : q));
      setEditing(null);
    } catch (_) {}
  };

  const remove = async (q) => {
    setConfirmDel(null);
    setQuests(p => p.filter(x => x._id !== q._id));
    try { await dispatch(deleteSideQuest(q._id)); } catch (_) { load(); }
  };

  const toggleDone = async (q) => {
    const next = !q.done;
    setQuests(p => p.map(x => x._id === q._id ? { ...x, done: next } : x));
    try { await dispatch(updateSideQuest(q._id, { done: next })); } catch (_) { load(); }
  };

  const wantTo = quests.filter(q => !q.done);
  const done   = quests.filter(q => q.done);

  const renderItem = (q) => (
    <div key={q._id} className={`sq2-item${q.done ? ' sq2-item--done' : ''}`}>
      <button
        className={`sq2-check${q.done ? ' sq2-check--on' : ''}`}
        onClick={() => toggleDone(q)}
        title={q.done ? 'Move back to Want to' : 'Mark as done'}
      />
      <div className="sq2-item-body">
        <h3 className="sq2-item-title">{q.title}</h3>
        {q.note && <p className="sq2-item-note">{q.note}</p>}
      </div>
      <div className="sq2-item-actions">
        <button className="sq2-icon-btn" onClick={() => openEdit(q)} title="Edit">
          <i className="bx bx-pencil" />
        </button>
        <button className="sq2-icon-btn sq2-icon-btn--del" onClick={() => setConfirmDel(q)} title="Delete">
          <i className="bx bx-trash" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="sq2-wrap">
      <header className="sq2-head">
        <h1 className="sq2-heading">Side Quests</h1>
        <p className="sq2-sub">Things you want to do because you can</p>
      </header>

      {/* Composer */}
      <div className="sq2-composer-zone">
        {!composerOpen ? (
          <button className="sq2-add-trigger" onClick={() => setComposerOpen(true)}>
            <span className="sq2-add-plus">+</span> Add a side quest
          </button>
        ) : (
          <div className="sq2-composer">
            <input
              ref={composerRef}
              className="sq2-composer-title"
              placeholder={PROMPTS[promptIdx.current]}
              value={newTitle}
              maxLength={TITLE_LIMIT}
              onChange={e => setNewTitle(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addQuest(); }}
            />
            <textarea
              className="sq2-composer-note"
              placeholder="Add a little more (optional)"
              value={newNote}
              maxLength={NOTE_LIMIT}
              rows={2}
              onChange={e => setNewNote(e.target.value)}
            />
            <div className="sq2-composer-actions">
              <button className="sq2-btn-text" onClick={() => { setComposerOpen(false); setNewTitle(''); setNewNote(''); }}>
                Cancel
              </button>
              <button className="sq2-btn-primary" onClick={addQuest} disabled={!newTitle.trim() || saving}>
                {saving ? 'Saving…' : 'Add'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sections */}
      {loading ? (
        <div className="sq2-loading"><span className="sq2-spinner" /> Loading…</div>
      ) : quests.length === 0 ? (
        <div className="sq2-empty">
          <p className="sq2-empty-title">Nothing here yet.</p>
          <p className="sq2-empty-text">Add the first thing you want to do.</p>
        </div>
      ) : (
        <>
          <section className="sq2-section">
            <h2 className="sq2-section-title">Want to <span className="sq2-section-count">{wantTo.length}</span></h2>
            {wantTo.length ? (
              <div className="sq2-list">{wantTo.map(renderItem)}</div>
            ) : (
              <p className="sq2-section-empty">All done. Add something new above.</p>
            )}
          </section>

          {done.length > 0 && (
            <section className="sq2-section">
              <h2 className="sq2-section-title">Done <span className="sq2-section-count">{done.length}</span></h2>
              <div className="sq2-list">{done.map(renderItem)}</div>
            </section>
          )}
        </>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="sq2-modal-overlay" onMouseDown={() => setEditing(null)}>
          <div className="sq2-modal" onMouseDown={e => e.stopPropagation()}>
            <p className="sq2-modal-eyebrow">Edit side quest</p>
            <input
              className="sq2-composer-title"
              value={editTitle}
              maxLength={TITLE_LIMIT}
              onChange={e => setEditTitle(e.target.value)}
              autoFocus
            />
            <textarea
              className="sq2-composer-note"
              value={editNote}
              maxLength={NOTE_LIMIT}
              rows={4}
              placeholder="Add a little more (optional)"
              onChange={e => setEditNote(e.target.value)}
            />
            <div className="sq2-composer-actions">
              <button className="sq2-btn-text" onClick={() => setEditing(null)}>Cancel</button>
              <button className="sq2-btn-primary" onClick={saveEdit} disabled={!editTitle.trim()}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {confirmDel && (
        <div className="sq2-modal-overlay" onMouseDown={() => setConfirmDel(null)}>
          <div className="sq2-confirm" onMouseDown={e => e.stopPropagation()}>
            <h3 className="sq2-confirm-title">Delete this side quest?</h3>
            <p className="sq2-confirm-text">"{confirmDel.title}" will be removed. This can't be undone.</p>
            <div className="sq2-composer-actions">
              <button className="sq2-btn-text" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button className="sq2-confirm-del" onClick={() => remove(confirmDel)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
